/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useRef, useState, useEffect, useMemo } from 'react';
import { Volume2, VolumeX, Plus, EyeOff } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { Link } from 'react-router-dom';
import { products, formatPrice } from '../data/products';
import { initialCMSContent } from '../data/cms';
import { getProducts } from '../utils/api';
import FAQSection from '../components/FAQSection';
import { getGoogleDrivePreviewUrl, normalizeVideoUrl } from '../utils/media';
import { applyCdn } from '../utils/mediaUrl';
import ProductCard from '../components/ProductCard';
import CTAButton from '../components/CTAButton';
import UGCPlayer from '../components/UGCPlayer';
import UGCProductCard from '../components/UGCProductCard';
import Hero from '../components/Hero';
import GlobalCTA from '../components/GlobalCTA';
import SEO from '../components/SEO';
import { useStorefrontData } from '../context/StorefrontDataContext';
import LiveStorefrontBar from '../components/live-editor/LiveStorefrontBar';
import EditableText from '../components/live-editor/EditableText';
import EditableRichText from '../components/live-editor/EditableRichText';
import EditableMedia from '../components/live-editor/EditableMedia';
import type { SectionDescriptor } from '../components/live-editor/LivePageDrawer';

export const HOMEPAGE_SECTIONS: SectionDescriptor[] = [
  { id: 'hero-section', label: '1. Hero Banner Video/Foto', key: 'hero' },
  { id: 'style-preference-section', label: '2. Pilihan Momen (Gaya Editorial)', key: 'stylePreference' },
  { id: 'showcase-section', label: '3. Koleksi Pilihan (Showcase)', key: 'showcase' },
  { id: 'ugc-section', label: '4. Video Komunitas (UGC)', key: 'ugc' },
  { id: 'faq-section', label: '5. Tanya Jawab (FAQ)', key: 'faq' },
  { id: 'cta-section', label: '6. Banner Penawaran (CTA)', key: 'cta' }
];

export function formatShowcaseTitle(title: string | undefined): string {
  if (!title) return '';
  const trimmed = title.trim();
  if (trimmed.includes('<span') || trimmed.includes('<em') || trimmed.includes('<strong') || trimmed.includes('<p')) {
    return trimmed;
  }
  if (trimmed.includes(' yang ')) {
    const [first, ...rest] = trimmed.split(' yang ');
    return `${first} yang <span style="color: #A1A1AA;">${rest.join(' yang ')}</span>`;
  }
  return trimmed;
}

export default function Home() {
  const ugcRef = useRef<HTMLDivElement>(null);
  const ugcSectionRef = useRef<HTMLElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const { cms: storefrontCms, cdnBaseUrl, isReady, isAdmin, isEditMode, isPreviewMode, updateCmsField } = useStorefrontData();
  const [activeUgcIndex, setActiveUgcIndex] = useState(0);
  const cms = storefrontCms || initialCMSContent;
  const [productsList, setProductsList] = useState<any[]>([]);
  const [isUgcSectionVisible, setIsUgcSectionVisible] = useState(false);

  const canEdit = isAdmin && isEditMode && !isPreviewMode;

  const isSectionHidden = (secId: string) => {
    const list = cms?.hiddenSections;
    return Array.isArray(list) && list.includes(secId);
  };

  const handleUpdateStyleItem = (idx: number, field: string, val: string) => {
    const nextItems = [...(cms.stylePreference?.items || [])];
    nextItems[idx] = { ...nextItems[idx], [field]: val };
    updateCmsField('stylePreference', 'items', nextItems);
  };

  const handleUgcScroll = () => {
    if (ugcRef.current) {
      const scrollLeft = ugcRef.current.scrollLeft;
      const itemWidth = window.innerWidth < 768 ? 280 + 16 : 320 + 16; // width + gap (gap-4 = 16px)
      const index = Math.round(scrollLeft / itemWidth);
      if (index !== activeUgcIndex) {
        setActiveUgcIndex(index);
      }
    }
  };

  const scrollToUgc = (index: number) => {
    if (ugcRef.current) {
      const itemWidth = window.innerWidth < 768 ? 280 + 16 : 320 + 16;
      ugcRef.current.scrollTo({
        left: index * itemWidth,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    async function loadProductsData() {
      try {
        const productsData = await getProducts('active', true);
        if (productsData && productsData.length > 0) {
          setProductsList(productsData);
        } else {
          setProductsList(products); // Fallback to static data if API is empty
        }
      } catch (error) {
        console.error('Failed to load homepage products:', error);
      }
    }

    loadProductsData();
  }, []);

  // Center UGC on section visibility
  useEffect(() => {
    if (isUgcSectionVisible && cms.ugc.items.length > 0) {
      const middleIndex = Math.floor(cms.ugc.items.length / 2);
      // Wait for layout to be ready after render
      const timer = setTimeout(() => {
        scrollToUgc(middleIndex);
        setActiveUgcIndex(middleIndex);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isUgcSectionVisible, cms.ugc.items]);

  useEffect(() => {
    const node = ugcSectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsUgcSectionVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px 0px' }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <SEO 
        title={cms.global?.siteTitle || cms.seo?.title}
        description={cms.global?.seoDescription || cms.seo?.description}
        keywords={cms.global?.seoKeywords}
        ogImage={cms.seo?.ogImage}
      />

      {/* Floating Action Top Bar for Live Editor (ADMIN ONLY) */}
      <LiveStorefrontBar pageName="Homepage" sections={HOMEPAGE_SECTIONS} />

      <div className="min-h-screen w-full bg-[#F9F9F9] flex flex-col font-sans transition-all">
        {(!isSectionHidden('hero-section') || canEdit) && (
          <section
            id="hero-section"
            className={`relative transition-all duration-300 ${
              isSectionHidden('hero-section') && canEdit ? 'opacity-40 grayscale-[15%] border-2 border-dashed border-amber-300' : ''
            }`}
          >
            {isSectionHidden('hero-section') && canEdit && (
              <div className="absolute top-4 left-6 z-40 bg-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow flex items-center gap-1.5">
                <EyeOff className="w-3.5 h-3.5" />
                <span>Seksi Hero disembunyikan dari publik</span>
              </div>
            )}
            <Hero
              title={cms.hero.title}
              subtitle={cms.hero.subtitle}
              cta={{
                text: cms.hero.cta,
                link: cms.hero.link || '/shop'
              }}
              imageUrl={applyCdn(cms.hero.imageUrl, cdnBaseUrl)}
              videoUrl={cms.hero.videoUrl}
              alignment="center"
              isLoading={!isReady}
              onEditTitle={(val) => updateCmsField('hero', 'title', val)}
              onEditSubtitle={(val) => updateCmsField('hero', 'subtitle', val)}
              onEditCtaText={(val) => updateCmsField('hero', 'cta', val)}
              onEditCtaLink={(val) => updateCmsField('hero', 'link', val)}
              onEditImageUrl={(val) => updateCmsField('hero', 'imageUrl', val)}
              onEditVideoUrl={(val) => updateCmsField('hero', 'videoUrl', val)}
            />
          </section>
        )}

        {/* Style Preference Section */}
        {(!isSectionHidden('style-preference-section') || canEdit) && (
          <section
            id="style-preference-section"
            className={`w-full bg-white relative transition-all duration-300 ${
              isSectionHidden('style-preference-section') && canEdit ? 'opacity-40 grayscale-[15%] border-2 border-dashed border-amber-300' : ''
            }`}
          >
            {isSectionHidden('style-preference-section') && canEdit && (
              <div className="absolute top-4 left-6 z-40 bg-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow flex items-center gap-1.5">
                <EyeOff className="w-3.5 h-3.5" />
                <span>Seksi Pilihan Momen / Gaya disembunyikan dari publik</span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2">
            {!isReady ? (
              [0, 1].map((idx) => (
                <div
                  key={idx}
                  className="relative w-full aspect-[3/4] max-h-[75vh] md:max-h-[85vh] overflow-hidden flex flex-col justify-end p-8 md:p-12 bg-paper animate-quiet-pulse border-r border-[#261f1f]/5 last:border-0"
                >
                  <div className="space-y-4">
                    <div className="h-4 bg-zinc-100/70 w-24 rounded-none" />
                    <div className="h-10 bg-zinc-100/70 w-48 rounded-none" />
                    <div className="h-10 bg-zinc-100/70 w-32 rounded-none" />
                  </div>
                </div>
              ))
            ) : (
              (cms.stylePreference?.items || []).map((item: any, index: number) => (
                <div
                  key={index}
                  className="relative w-full aspect-[3/4] max-h-[75vh] md:max-h-[85vh] overflow-hidden flex flex-col justify-end p-8 md:p-12 group"
                >
                  {/* Background Image wrapped with EditableMedia if in edit mode */}
                  <EditableMedia
                    currentImageUrl={item.imageUrl}
                    onImageChange={(url) => handleUpdateStyleItem(index, 'imageUrl', url)}
                    label={`Gaya ${item.title || index + 1}`}
                    className="absolute inset-0 w-full h-full z-0"
                  >
                    <div className="absolute inset-0 z-0 bg-paper">
                      <motion.img
                        src={item.imageUrl}
                        alt={item.title}
                        initial={{ scale: 1.15, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
                        className="w-full h-full object-cover object-top grayscale-[20%] group-hover:grayscale-0 transition-all duration-700"
                      />
                      {/* Overlays */}
                      <div className="absolute inset-0 bg-black/25 group-hover:bg-black/15 transition-colors duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    </div>
                  </EditableMedia>

                  {/* Content */}
                  <div className="relative z-10">
                    {canEdit ? (
                      <div className="space-y-2 mb-4">
                        <EditableRichText
                          value={item.subtitle}
                          onChange={(val) => handleUpdateStyleItem(index, 'subtitle', val)}
                          as="p"
                          label="Subjudul Kartu"
                          className="text-label text-white/80 uppercase tracking-widest text-xs md:text-sm"
                        />
                        <EditableRichText
                          value={item.title}
                          onChange={(val) => handleUpdateStyleItem(index, 'title', val)}
                          as="h2"
                          label="Judul Kartu"
                          className="text-5xl md:text-7xl font-serif text-white"
                        />
                        <div className="pt-2">
                          <EditableText
                            value={item.cta}
                            onChange={(val) => handleUpdateStyleItem(index, 'cta', val)}
                            as="span"
                            label="Teks CTA"
                            className="inline-block text-xs font-semibold px-4 py-2 bg-white/20 text-white rounded border border-white/30"
                          />
                        </div>
                      </div>
                    ) : (
                      <Link to={item.link}>
                        <motion.p
                          initial={{ opacity: 0, y: 15 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
                          className="text-label text-white/80 mb-2 uppercase tracking-widest text-xs md:text-sm"
                          dangerouslySetInnerHTML={{ __html: item.subtitle }}
                        />
                        <motion.h2
                          initial={{ opacity: 0, y: 25 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: 0.35, ease: [0.25, 1, 0.5, 1] }}
                          className="text-5xl md:text-7xl font-serif text-white mb-8"
                          dangerouslySetInnerHTML={{ __html: item.title }}
                        />
                        <motion.div
                          initial={{ opacity: 0, y: 15 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: 0.5, ease: [0.25, 1, 0.5, 1] }}
                        >
                          <CTAButton 
                            as="div"
                            variant="transparent"
                          >
                            {item.cta}
                          </CTAButton>
                        </motion.div>
                      </Link>
                    )}
                  </div>

                  {/* Border line between items */}
                  {index === 0 && (
                    <div className="hidden md:block absolute right-0 top-0 bottom-0 w-[1px] bg-white/10 z-20" />
                  )}
                </div>
              ))
            )}
          </div>
        </section>
      )}
      </div>

      {/* Product Grid / Showcase Section */}
      {(!isSectionHidden('showcase-section') || canEdit) && (
        <section
          id="showcase-section"
          className={`pt-20 pb-24 px-6 md:px-12 max-w-[1600px] mx-auto relative transition-all duration-300 ${
            isSectionHidden('showcase-section') && canEdit ? 'opacity-40 grayscale-[15%] border-2 border-dashed border-amber-300' : ''
          }`}
        >
          {isSectionHidden('showcase-section') && canEdit && (
            <div className="absolute top-4 left-6 z-40 bg-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow flex items-center gap-1.5">
              <EyeOff className="w-3.5 h-3.5" />
              <span>Seksi Showcase disembunyikan dari publik</span>
            </div>
          )}
        <div className="max-w-4xl mr-auto mb-16 md:mb-20">
          {!isReady ? (
            <div className="space-y-4 animate-quiet-pulse">
              <div className="h-4 bg-zinc-100/70 w-24 rounded-none" />
              <div className="h-10 md:h-12 bg-zinc-100/70 w-3/4 rounded-none" />
              <div className="h-6 bg-zinc-100/70 w-1/2 rounded-none" />
            </div>
          ) : (
            <>
              {canEdit ? (
                <div className="space-y-4">
                  <EditableText
                    value={cms.showcase.label}
                    onChange={(val) => updateCmsField('showcase', 'label', val)}
                    as="p"
                    label="Showcase Label"
                    className="text-label text-[#722F38]/70"
                  />
                  <EditableRichText
                    value={formatShowcaseTitle(cms.showcase.title)}
                    onChange={(val) => updateCmsField('showcase', 'title', val)}
                    as="h2"
                    multiline
                    label="Showcase Headline"
                    className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#6E2B30] leading-tight max-w-3xl"
                  />
                  <EditableRichText
                    value={cms.showcase.description}
                    onChange={(val) => updateCmsField('showcase', 'description', val)}
                    as="p"
                    multiline
                    label="Showcase Deskripsi"
                    className="text-[#3A3A3A]/80 max-w-2xl text-sm md:text-base leading-relaxed"
                  />
                </div>
              ) : (
                <>
                  <motion.p 
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 1, ease: [0.25, 1, 0.5, 1] }}
                    className="text-label text-[#722F38]/55 mb-4"
                  >
                    {cms.showcase.label}
                  </motion.p>
                  <motion.h2
                    initial={{ opacity: 0, y: 35 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 1.2, delay: 0.1, ease: [0.25, 1, 0.5, 1] }}
                    className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#6E2B30] leading-tight max-w-3xl"
                    dangerouslySetInnerHTML={{ __html: formatShowcaseTitle(cms.showcase.title) }}
                  />
                  <motion.p 
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 1, delay: 0.25, ease: [0.25, 1, 0.5, 1] }}
                    className="text-[#3A3A3A]/70 mt-5 max-w-2xl"
                    dangerouslySetInnerHTML={{ __html: cms.showcase.description }}
                  />
                </>
              )}
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-16">
          {!isReady || productsList.length === 0 ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className="flex flex-col animate-quiet-pulse">
                <div className="aspect-[3/4] w-full bg-zinc-100/70 mb-4 rounded-none" />
                <div className="h-3 bg-zinc-100/70 w-1/3 mb-1.5 rounded-none" />
                <div className="h-4 bg-zinc-100/70 w-3/4 mb-2 rounded-none" />
                <div className="h-4 bg-zinc-100/70 w-1/2 mb-3 rounded-none" />
              </div>
            ))
          ) : (
            (cms.showcase.productIds?.length > 0
              ? productsList.filter(p => cms.showcase.productIds.includes(p.id))
              : productsList.slice(0, 4)
            ).map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))
          )}
          </div>
        </section>
      )}

      {/* UGC Video Section - Infinite Scrolling Carousel */}
      {(!isSectionHidden('ugc-section') || canEdit) && (
        <section
          id="ugc-section"
          ref={ugcSectionRef}
          className={`py-20 overflow-hidden bg-white relative transition-all duration-300 ${
            isSectionHidden('ugc-section') && canEdit ? 'opacity-40 grayscale-[15%] border-2 border-dashed border-amber-300' : ''
          }`}
        >
          {isSectionHidden('ugc-section') && canEdit && (
            <div className="absolute top-4 left-6 z-40 bg-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow flex items-center gap-1.5">
              <EyeOff className="w-3.5 h-3.5" />
              <span>Seksi UGC Video disembunyikan dari publik</span>
            </div>
          )}
        <div className="px-6 md:px-12 max-w-[1600px] mx-auto mb-12 flex items-end justify-between">
          <div>
            {!isReady ? (
              <div className="space-y-4 animate-quiet-pulse">
                <div className="h-10 bg-zinc-100/70 w-64 rounded-none" />
                <div className="h-4 bg-zinc-100/70 w-96 rounded-none" />
              </div>
            ) : (
              <>
                {canEdit ? (
                  <div className="space-y-3">
                    <EditableRichText
                      value={cms.ugc.title}
                      onChange={(val) => updateCmsField('ugc', 'title', val)}
                      as="h2"
                      multiline
                      label="Judul UGC"
                      className="text-3xl md:text-5xl font-serif text-brand-wine"
                    />
                    <EditableRichText
                      value={cms.ugc.subtitle}
                      onChange={(val) => updateCmsField('ugc', 'subtitle', val)}
                      as="p"
                      multiline
                      label="Subjudul UGC"
                      className="text-muted max-w-xl"
                    />
                  </div>
                ) : (
                  <>
                    <motion.h2 
                      initial={{ opacity: 0, y: 25 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, ease: [0.25, 1, 0.5, 1] }}
                      className="text-3xl md:text-5xl font-serif text-brand-wine mb-4"
                      dangerouslySetInnerHTML={{ __html: cms.ugc.title }}
                    />
                    <motion.div 
                      initial={{ opacity: 0, y: 15 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
                      className="text-muted max-w-xl"
                      dangerouslySetInnerHTML={{ __html: cms.ugc.subtitle }}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="relative">
          {/* Focal Carousel Container */}
          <div
            ref={ugcRef}
            className="flex gap-4 overflow-x-auto hide-scrollbar snap-x snap-mandatory px-[calc(50%-140px-8px)] md:px-[calc(50%-160px-8px)] py-10"
            onScroll={handleUgcScroll}
          >
            {(isUgcSectionVisible ? (cms.ugc.items?.length > 0 ? cms.ugc.items : []) : []).map((ugcItem, index) => {
              const product = productsList.find((p) => p.id === ugcItem.productId);
              const variants = product?.product_variants || product?.variants || [];
              const firstVariant = variants[0];
              const thumbnail = ugcItem.thumbnailUrl || product?.thumbnail_url || firstVariant?.image_url;
              const isActive = activeUgcIndex === index;

              return (
                <motion.div
                  key={index}
                  animate={{
                    scale: isActive ? 1.1 : 0.92,
                    opacity: isActive ? 1 : 0.6,
                  }}
                  transition={{ duration: 0.4 }}
                  className={`flex flex-col shrink-0 w-[280px] md:w-[320px] snap-center ${!isActive ? 'cursor-pointer hover:opacity-80' : ''}`}
                  onClick={() => {
                    if (!isActive) {
                      scrollToUgc(index);
                    }
                  }}
                >
                  <UGCPlayer
                    videoUrl={ugcItem.videoUrl}
                    thumbnail={thumbnail}
                    isActive={isActive}
                  />

                  <UGCProductCard
                    product={product}
                    thumbnail={thumbnail}
                    isActive={isActive}
                  />
                </motion.div>
              );
            })}
          </div>

          {!isUgcSectionVisible || !isReady ? (
            <div className="flex gap-4 overflow-hidden px-[calc(50%-140px-8px)] md:px-[calc(50%-160px-8px)] py-10">
              {[0, 1, 2].map((item) => (
                <div key={item} className="flex flex-col shrink-0 w-[280px] md:w-[320px] opacity-60 animate-quiet-pulse">
                  <div className="aspect-[9/16] w-full bg-zinc-100/70" />
                  <div className="mt-1 h-[78px] bg-zinc-100/50" />
                </div>
              ))}
            </div>
          ) : null}

          {/* Navigation Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {cms.ugc.items.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToUgc(i)}
                aria-label={`Lihat video pelanggan ke-${i + 1}`}
                className={`relative w-2 h-2 transition-all ${activeUgcIndex === i ? 'w-8 bg-brand-wine' : 'bg-zinc-200'} rounded-none before:content-[''] before:absolute before:-top-5 before:-bottom-5 before:-left-5 before:-right-5`}
              />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* FAQ Section */}
      {(!isSectionHidden('faq-section') || canEdit) && (
        <div
          id="faq-section"
          className={`relative transition-all duration-300 ${
            isSectionHidden('faq-section') && canEdit ? 'opacity-40 grayscale-[15%] border-2 border-dashed border-amber-300' : ''
          }`}
        >
          {isSectionHidden('faq-section') && canEdit && (
            <div className="absolute top-4 left-6 z-40 bg-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow flex items-center gap-1.5">
              <EyeOff className="w-3.5 h-3.5" />
              <span>Seksi Tanya Jawab (FAQ) disembunyikan dari publik</span>
            </div>
          )}
          <FAQSection />
        </div>
      )}

      {/* Global CTA Section */}
      {(!isSectionHidden('cta-section') || canEdit) && (
        <div
          id="cta-section"
          className={`relative transition-all duration-300 ${
            isSectionHidden('cta-section') && canEdit ? 'opacity-40 grayscale-[15%] border-2 border-dashed border-amber-300' : ''
          }`}
        >
          {isSectionHidden('cta-section') && canEdit && (
            <div className="absolute top-4 left-6 z-40 bg-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow flex items-center gap-1.5">
              <EyeOff className="w-3.5 h-3.5" />
              <span>Seksi Banner Penawaran (CTA) disembunyikan dari publik</span>
            </div>
          )}
          <GlobalCTA />
        </div>
      )}
    </>
  );
}
